import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Search, ChevronDown } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useAppMode } from "@/contexts/AppModeContext";
import { useIsTablet } from "@/hooks/use-mobile";
import { useUserMenuData, UserMenuItems } from "./UserMenu";
import { LocationSelector } from "./LocationSelector";
import { NotificationsBell } from "./NotificationsBell";

export function SiteHeader() {
  const { t: lt } = useLingui();
  const { mode, setMode } = useAppMode();
  // Profile lives in the TopBar (top-right) on desktop; on tablet it moves to
  // the sidebar rail, so it's hidden here.
  const isTablet = useIsTablet();
  const { displayName, displayEmail, initials } = useUserMenuData();

  return (
    <header className="sticky top-0 z-40 flex h-14 items-center gap-2 px-2 transition-[width,height] ease-linear md:h-[68px] md:gap-3 md:px-3 group-has-data-[collapsible=icon]/sidebar-wrapper:h-[60px]">
      <SidebarTrigger className="mr-1 size-9 rounded-lg border border-[var(--shell-border)] bg-[var(--shell-control-bg)] text-[var(--shell-control-active-fg)] hover:bg-(--color-surface-accent) md:hidden" />
      <Separator
        orientation="vertical"
        className="hidden mx-2 data-[orientation=vertical]:h-4 md:block"
      />
      <div className="relative hidden w-56 md:block lg:w-64">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-(--color-text-secondary)" />
        <Input
          aria-label={t`Search`}
          placeholder={t`Search…`}
          className="h-11 rounded-lg border-[var(--shell-border)] bg-[var(--shell-control-bg)] pl-8 text-sm shadow-[var(--shell-shadow)]"
        />
      </div>
      <div className="flex-1" />
      {mode === "working" && (
        <>
          <LocationSelector
            align="end"
            triggerClassName="hidden h-11 rounded-lg border border-(--shell-border) bg-(--shell-control-bg) px-3 text-sm text-(--color-text-primary) transition-colors hover:bg-(--color-surface-accent) sm:inline-flex"
          />
          <Separator
            orientation="vertical"
            className="mx-1 hidden data-[orientation=vertical]:h-6 sm:block"
          />
        </>
      )}
      <div className="hidden items-center gap-2 xl:flex">
        <span className="text-xs text-(--color-text-secondary) mr-1">
          {lt`Mode`}
        </span>
        <div className="flex h-11 items-center rounded-lg bg-[var(--shell-muted-bg)] p-1">
          <button
            type="button"
            onClick={() => setMode("working")}
            className={`h-9 rounded-lg px-3 text-xs font-medium transition-colors ${
              mode === "working"
                ? "bg-[var(--shell-control-bg)] text-[var(--shell-control-active-fg)] shadow-sm"
                : "text-(--color-text-secondary)"
            }`}
          >
            {lt`Working`}
          </button>
          <button
            type="button"
            onClick={() => setMode("build")}
            className={`h-9 rounded-lg px-3 text-xs font-medium transition-colors ${
              mode === "build"
                ? "bg-[var(--shell-control-bg)] text-[var(--shell-control-active-fg)] shadow-sm"
                : "text-(--color-text-secondary)"
            }`}
          >
            {lt`Build`}
          </button>
        </div>
      </div>
      <NotificationsBell />
      {!isTablet && (
        <>
          <Separator
            orientation="vertical"
            className="mx-1 hidden data-[orientation=vertical]:h-6 sm:block"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-lg border border-(--shell-border) bg-(--shell-control-bg) px-2 py-1.5 outline-none transition-colors hover:bg-(--color-surface-accent) focus-visible:ring-2 focus-visible:ring-(--color-ring) md:mr-4">
                <Avatar className="h-7 w-7 border border-(--color-border) md:h-8 md:w-8">
                  <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                </Avatar>
                <div className="hidden md:flex flex-col text-left leading-tight">
                  <span className="text-sm font-medium text-(--color-text-primary)">
                    {displayName}
                  </span>
                  {displayEmail && (
                    <span className="text-[11px] text-(--color-text-secondary)">
                      {displayEmail}
                    </span>
                  )}
                </div>
                <ChevronDown className="hidden md:block size-4 text-(--color-text-secondary)" />
              </button>
            </DropdownMenuTrigger>
            <UserMenuItems align="end" />
          </DropdownMenu>
        </>
      )}
    </header>
  );
}
